<script setup lang="ts">
/**
 * Корневой компонент: одна страница с двумя состояниями.
 *
 * Пока проверки идут — список шагов с прогрессом. Когда отчёт готов —
 * результат. Роутера нет намеренно: сервис состоит из одного экрана, и
 * добавлять vue-router ради двух состояний незачем.
 */

import { onMounted, ref } from 'vue';
import type { Report } from '@vpncheck/shared';
import CheckProgress from './components/CheckProgress.vue';
import ReportView from './components/ReportView.vue';
import { fetchReport } from './api.js';
import { STEP_TITLES, runAllChecks, type StepId, type StepState } from './checks/runner.js';

type Phase = 'idle' | 'running' | 'done' | 'error' | 'loading';

const phase = ref<Phase>('idle');
const steps = ref<StepState[]>([]);
const report = ref<Report | null>(null);
const token = ref<string | null>(null);
const errorText = ref<string | null>(null);
const portScanConsent = ref(false);

function resetSteps(): void {
  steps.value = (Object.keys(STEP_TITLES) as StepId[]).map((id) => ({
    id,
    title: STEP_TITLES[id],
    status: 'pending',
  }));
}

function updateStep(id: StepId, status: StepState['status'], note?: string): void {
  const next = steps.value.map((step) =>
    step.id === id ? { ...step, status, ...(note ? { note } : {}) } : step,
  );
  steps.value = next;
}

/**
 * Открытие сохранённого отчёта по ссылке `?token=…`.
 *
 * Проверки при этом не запускаются: отчёт уже собран и лежит в БД. Это важно —
 * иначе переход по ссылке перезаписал бы чужой отчёт результатами того, кто её
 * открыл.
 */
onMounted(async () => {
  const saved = new URLSearchParams(window.location.search).get('token');
  if (!saved) return;

  phase.value = 'loading';
  try {
    const { report: loaded } = await fetchReport(saved);
    token.value = saved;
    report.value = loaded;
    phase.value = 'done';
  } catch (err) {
    errorText.value = `Не удалось открыть отчёт по ссылке: ${message(err)}`;
    phase.value = 'error';
  }
});

async function start(): Promise<void> {
  // Токен из адресной строки убираем: дальше показывается новый отчёт, и старая
  // ссылка в адресе вводила бы в заблуждение при перезагрузке.
  if (window.location.search) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  phase.value = 'running';
  errorText.value = null;
  report.value = null;
  resetSteps();

  try {
    const outcome = await runAllChecks({
      portScanConsent: portScanConsent.value,
      onProgress: updateStep,
    });
    token.value = outcome.token;
    report.value = outcome.report;
    phase.value = 'done';
  } catch (err) {
    errorText.value = message(err);
    phase.value = 'error';
  }
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
</script>

<template>
  <div class="page">
    <header class="header">
      <h1>Насколько заметен ваш VPN</h1>
      <p class="lead">
        Страница выполняет те же проверки, которыми пользуются сайты и антифрод-системы,
        и показывает, что именно выдаёт использование VPN или прокси. Данные никуда не
        передаются, кроме этого сервиса.
      </p>
    </header>

    <section v-if="phase === 'loading'" class="card">
      <p class="note">Загружается сохранённый отчёт…</p>
    </section>

    <section v-if="phase === 'idle'" class="card start">
      <label class="consent">
        <input v-model="portScanConsent" type="checkbox" />
        <span>
          Проверить открытые порты на моём выходном IP
          <small>
            Сервис подключится к типовым портам прокси и VPN на вашем адресе. Проверка
            затрагивает только ваш собственный IP.
          </small>
        </span>
      </label>
      <button type="button" class="primary" @click="start">Начать проверку</button>
      <p class="note">
        Проверка занимает около 15 секунд: часть тестов ждёт таймаута по заблокированным
        ресурсам.
      </p>
    </section>

    <CheckProgress v-if="phase === 'running'" :steps="steps" />

    <section v-if="phase === 'error'" class="card error">
      <h2>Проверка не завершилась</h2>
      <p>{{ errorText }}</p>
      <button type="button" class="primary" @click="start">Повторить</button>
    </section>

    <ReportView
      v-if="phase === 'done' && report"
      :report="report"
      :token="token"
      @restart="start"
    />

    <footer class="footer">
      <p>
        Чего эта страница не видит: установленные приложения, сетевые интерфейсы вашей
        системы и список процессов. Мобильные приложения проверяют именно их, поэтому
        отчёт описывает только то, что доступно сайту в браузере.
      </p>
    </footer>
  </div>
</template>

<style scoped>
.page {
  max-width: 880px;
  margin: 0 auto;
  padding: 32px 20px 64px;
}

.header h1 {
  margin: 0 0 12px;
  font-size: 28px;
  font-weight: 650;
  letter-spacing: -0.01em;
}

.lead {
  margin: 0;
  color: var(--text-muted);
  line-height: 1.6;
}

.card {
  margin-top: 24px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
}

.start {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
}

.consent {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  line-height: 1.5;
}

.consent input {
  margin-top: 3px;
}

.consent small {
  display: block;
  margin-top: 4px;
  color: var(--text-muted);
}

.primary {
  padding: 10px 18px;
  border: 0;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-weight: 550;
  cursor: pointer;
}

.primary:hover {
  background: var(--accent-hover);
}

.note {
  margin: 0;
  color: var(--text-muted);
  font-size: 14px;
}

.error h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.error p {
  margin: 0 0 16px;
  color: var(--severity-high);
}

.footer {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.6;
}

.footer p {
  margin: 0;
}
</style>
